import { useCallback, useMemo, useState } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useIsLiveDocViewMode } from './live/useIsLiveDocViewMode.ts';
import { selectPublishedSuggestion } from './published/publishedSuggestionsStore.ts';
import { usePublishedSuggestionsState } from './published/usePublishedSuggestionsState.ts';
import { getVisibleSuggestions } from './suggestionsPanelFilters.ts';
import { useSuggestionsPanelFilters } from './useSuggestionsPanelFilters.ts';

type AISuggestionsSharedState = NonNullable<
	ReturnType<NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']>
>;

export type SuggestionsSourceMode = 'editor' | 'published';

export type SuggestionsThinkingState = {
	status: AISuggestionsSharedState['thinkingStatus'] | undefined;
	startedAt: number | null;
	progressTarget: AISuggestionsSharedState['thinkingProgressTarget'] | null;
	agentReadyAt: number | null;
	reviewingAgent: NonNullable<AISuggestionsSharedState['reviewingAgent']> | undefined;
	regeneratingSuggestionIds: AISuggestionsSharedState['regeneratingSuggestionIds'] | undefined;
};

export type SuggestionsSource = {
	mode: SuggestionsSourceMode;
	/** Suggestions are listed but can only be acted on in edit mode. */
	isReadOnly: boolean;
	count: number;
	suggestions: Suggestion[];
	visibleSuggestions: Suggestion[];
	selectedSuggestionId: string | undefined;
	selectSuggestion: (suggestionId: string | undefined) => void;
	isThinking: boolean;
	thinking: SuggestionsThinkingState | undefined;
	isInitialising: boolean;
	hasError: boolean;
	editorAPI: FullPageEditorPresetAPI | undefined;
};

const NO_SUGGESTIONS: Suggestion[] = [];

const getOrderedSuggestions = (aiSuggestionsState: AISuggestionsSharedState): Suggestion[] => {
	const suggestions: Suggestion[] = [];
	for (const id of aiSuggestionsState.orderedSuggestionIds) {
		const suggestion = aiSuggestionsState.suggestionById.get(id);
		if (suggestion !== undefined) {
			suggestions.push(suggestion);
		}
	}
	return suggestions;
};

export const useSuggestionsSourceMode = (): SuggestionsSourceMode => {
	const { contentId } = usePublishedSuggestionsState();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	if (
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- The suggested edits experiment is the kill switch for the view mode Suggestions panel subgate.
		(contentId !== undefined || !fullPageEditorPresetAPI) &&
		expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- View mode Suggestions panel subgate.
		fg('confluence_suggestions_panel_view_mode')
	) {
		return 'published';
	}
	return 'editor';
};

export const useSuggestionsSource = (): SuggestionsSource => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const mode = useSuggestionsSourceMode();
	const publishedState = usePublishedSuggestionsState();
	const [{ currentFilter, currentSort }] = useSuggestionsPanelFilters();
	const { userId } = useSessionData();
	const isLiveDocViewMode = useIsLiveDocViewMode();
	const editorAPI = mode === 'editor' ? fullPageEditorPresetAPI : undefined;
	const editorState = useSharedPluginStateWithSelector(
		editorAPI,
		['aiSuggestions'],
		({ aiSuggestionsState }: { aiSuggestionsState?: AISuggestionsSharedState }) => ({
			suggestions: aiSuggestionsState ? getOrderedSuggestions(aiSuggestionsState) : [],
			selectedSuggestionId: aiSuggestionsState?.selectedSuggestion,
			thinkingStatus: aiSuggestionsState?.thinkingStatus,
			// Shared thinking-session anchor so the inline bar stays in sync with
			// the editor's thinking bar across mount/unmount.
			thinkingStartedAt: aiSuggestionsState?.thinkingStartedAt ?? null,
			thinkingProgressTarget: aiSuggestionsState?.thinkingProgressTarget ?? null,
			// When the reviewing agent became available in this session; anchors the loading
			// copy's rotation (see `loadingRotationStartedAt`).
			thinkingAgentReadyAt: aiSuggestionsState?.thinkingAgentReadyAt ?? null,
			// The full agent, for its avatar in the inline loading bar. Returned as the
			// plugin's object so the shallow comparison only changes when the agent does.
			reviewingAgent: aiSuggestionsState?.reviewingAgent ?? undefined,
			// Returned as the Set itself rather than a derived array — the hook
			// compares selector results shallowly, and this identity only changes
			// when the plugin swaps the set.
			regeneratingSuggestionIds: aiSuggestionsState?.regeneratingSuggestionIds,
		}),
	);

	// Selecting through the editor would show the diff, so live doc view mode selects here.
	const [liveDocViewSelectedSuggestionId, setLiveDocViewSelectedSuggestionId] = useState<
		string | undefined
	>();
	const [wasLiveDocViewMode, setWasLiveDocViewMode] = useState(isLiveDocViewMode);
	if (wasLiveDocViewMode !== isLiveDocViewMode) {
		setWasLiveDocViewMode(isLiveDocViewMode);
		setLiveDocViewSelectedSuggestionId(undefined);
	}

	const selectSuggestion = useCallback(
		(suggestionId: string | undefined) => {
			if (mode === 'published') {
				selectPublishedSuggestion(suggestionId);
				return;
			}
			if (isLiveDocViewMode) {
				setLiveDocViewSelectedSuggestionId(suggestionId);
				return;
			}
			editorAPI?.aiSuggestions?.actions.selectSuggestion(suggestionId ?? null, {
				interactionPoint: 'sidebar',
			});
		},
		[editorAPI, isLiveDocViewMode, mode],
	);

	const suggestions =
		mode === 'published'
			? publishedState.suggestions
			: (editorState?.suggestions ?? NO_SUGGESTIONS);
	const visibleSuggestions = useMemo(
		() =>
			getVisibleSuggestions(suggestions, { currentFilter, currentSort, currentUserId: userId }) ??
			[],
		[suggestions, currentFilter, currentSort, userId],
	);

	if (mode === 'published') {
		return {
			mode: 'published',
			isReadOnly: true,
			count: suggestions.length,
			suggestions,
			visibleSuggestions,
			selectedSuggestionId: publishedState.selectedSuggestionId,
			selectSuggestion,
			isThinking: false,
			thinking: undefined,
			isInitialising: publishedState.status === 'idle' || publishedState.status === 'loading',
			hasError: publishedState.status === 'error',
			editorAPI: undefined,
		};
	}

	return {
		mode: 'editor',
		isReadOnly: isLiveDocViewMode,
		count: suggestions.length,
		suggestions,
		visibleSuggestions,
		selectedSuggestionId: isLiveDocViewMode
			? liveDocViewSelectedSuggestionId
			: editorState.selectedSuggestionId,
		selectSuggestion,
		isThinking: editorState.thinkingStatus === 'thinking',
		thinking: {
			status: editorState.thinkingStatus,
			startedAt: editorState.thinkingStartedAt,
			progressTarget: editorState.thinkingProgressTarget,
			agentReadyAt: editorState.thinkingAgentReadyAt,
			reviewingAgent: editorState.reviewingAgent,
			regeneratingSuggestionIds: editorState.regeneratingSuggestionIds,
		},
		isInitialising: false,
		hasError: false,
		editorAPI,
	};
};
